import { useEffect, useRef, useState } from "react";
import { API, authHeaders } from "../lib/api";
import { X, Sparkle, PaperPlaneRight } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Where are our biggest bottlenecks?",
  "Which capabilities have the highest AI opportunity?",
  "What foundational work must precede AI deployment?",
  "Summarise the loan origination workflow issues",
];

export function CopilotPanel({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    if (!text.trim() || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/copilot/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const obj = JSON.parse(payload);
          if (obj.session_id) setSessionId(obj.session_id);
          if (obj.delta) setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + obj.delta };
            return copy;
          });
          if (obj.error) setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: "Copilot error: " + obj.error };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", content: "Connection error. Please try again." }]);
    }
    setStreaming(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-y-0 right-0 w-[420px] z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-l border-[#D4AF37]/30 flex flex-col fade-up" data-testid="copilot-panel">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2 text-[#D4AF37]">
          <Sparkle size={18} weight="fill" />
          <span className="font-display font-bold tracking-tight text-white">Enterprise Engineering Copilot</span>
        </div>
        <button onClick={onClose} data-testid="close-copilot-btn" className="text-white/60 hover:text-white transition-colors"><X size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="space-y-2">
            <p className="text-white/60 text-sm">Grounded in your workspace evidence only. Assumptions are labelled; approvals stay human.</p>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} data-testid="copilot-suggestion"
                className="block w-full text-left text-[13px] text-[#D4AF37] border border-[#D4AF37]/30 rounded-sm px-3 py-2 hover:bg-[#D4AF37]/10 transition-colors">{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : ""}>
            <div className={`inline-block max-w-[90%] text-left text-[13px] leading-relaxed whitespace-pre-wrap rounded-sm px-3 py-2 ${m.role === "user" ? "bg-[#D4AF37] text-[#0A0A0A]" : "bg-white/10 text-white/90"}`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="p-4 border-t border-white/10 flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="copilot-input"
          placeholder="Ask about your operating model…"
          className="flex-1 bg-white/10 text-white text-sm rounded-sm px-3 py-2 outline-none focus:ring-1 focus:ring-[#D4AF37] placeholder:text-white/40" />
        <button type="submit" disabled={streaming} data-testid="copilot-send-btn"
          className="bg-[#D4AF37] text-[#0A0A0A] px-3 rounded-sm disabled:opacity-50 hover:bg-[#e0c258] transition-colors"><PaperPlaneRight size={16} weight="fill" /></button>
      </form>
    </div>
  );
}
